import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	FORM_TABLE_MAX_ROWS,
	isFormTableAnswer,
	type FormFieldDefinitionModel,
	type FormTableColumnModel,
	type FormTableRowModel,
} from '@supply-mover-2-0/shared';
import type React from 'react';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { Button } from '../Button';
import { Input } from '../Input';
import { Select } from '../Select';

/** Names for a table's item and model cells, and an item's usual price. */
export interface FormTableLookupsModel {
	items: ReadonlyArray<{ id: number; name: string; price?: number | null }>;
	models: ReadonlyArray<{ id: number; name: string }>;
}

interface FormTableQuestionProps {
	field: FormFieldDefinitionModel;
	value: unknown;
	/** Staff entering the rows before sending; the customer only reads them. */
	editable: boolean;
	lookups: FormTableLookupsModel | null;
	onChange: (rows: Array<FormTableRowModel>) => void;
	disabled?: boolean;
	id?: string;
}

/**
 * PD-13768 — a table question: rows with fixed columns (equipment, products and counts,
 * agreed prices). Staff fill the rows before a form is sent (`editable`); on the
 * customer's page and in previews the same rows are drawn read-only, items and models by
 * name and prices as money. Each row is a set of cell values keyed by column, as text,
 * exactly the shape the shared validator cleans.
 */
export function FormTableQuestion({
	field,
	value,
	editable,
	lookups,
	onChange,
	disabled = false,
	id,
}: FormTableQuestionProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const columns = field.fieldOptions?.table?.columns ?? [];
	const maxRows = field.fieldOptions?.table?.maxRows ?? FORM_TABLE_MAX_ROWS;
	const rows: Array<FormTableRowModel> = isFormTableAnswer(value) ? value : [];

	const cellText = (column: FormTableColumnModel, cell: string | undefined): string => {
		if (cell == null || cell === '') {
			return '';
		}
		switch (column.kind) {
			case 'item':
				return lookups?.items.find((item) => String(item.id) === cell)?.name ?? cell;
			case 'model':
				return lookups?.models.find((model) => String(model.id) === cell)?.name ?? cell;
			case 'currency': {
				const amount = Number(cell);
				return Number.isFinite(amount)
					? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
					: cell;
			}
			default:
				return cell;
		}
	};

	const update = (index: number, key: string, next: string): void => {
		const copy = rows.map((row) => ({ ...row }));
		const row = copy[index] ?? {};
		if (next === '') {
			delete row[key];
		} else {
			row[key] = next;
		}
		// Picking a product fills its usual price when the row has none yet.
		const column = columns.find((candidate) => candidate.key === key);
		const priceColumn = columns.find((candidate) => candidate.kind === 'currency');
		if (column?.kind === 'item' && priceColumn != null && (row[priceColumn.key] ?? '') === '') {
			const price = lookups?.items.find((item) => String(item.id) === next)?.price;
			if (price != null) {
				row[priceColumn.key] = price.toFixed(2);
			}
		}
		copy[index] = row;
		onChange(copy);
	};

	if (!editable) {
		if (rows.length === 0) {
			return (
				<p id={id} className={cn('text-sm', colors.classes.textSecondary)}>
					{t('None')}
				</p>
			);
		}
		return (
			<div id={id} className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className={cn('border-b text-left', colors.classes.borderDefault)}>
							{columns.map((column) => (
								<th
									key={column.key}
									scope="col"
									className={cn('py-1.5 pr-3 text-xs font-semibold', colors.classes.textSecondary)}
								>
									{t(column.label)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr
								key={index}
								className={cn('border-b last:border-b-0', colors.classes.borderDefault)}
							>
								{columns.map((column) => (
									<td key={column.key} className={cn('py-1.5 pr-3 align-top', colors.classes.textPrimary)}>
										{t(cellText(column, row[column.key]))}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	return (
		<div id={id} className="space-y-2">
			{rows.length > 0 && (
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="text-left">
								{columns.map((column) => (
									<th
										key={column.key}
										scope="col"
										className={cn('pb-1 pr-2 text-xs font-semibold', colors.classes.textSecondary)}
									>
										{t(column.label)}
									</th>
								))}
								<th className="w-8" aria-label={t('Remove')} />
							</tr>
						</thead>
						<tbody>
							{rows.map((row, index) => (
								<tr key={index}>
									{columns.map((column) => (
										<td key={column.key} className="py-1 pr-2 align-top">
											{column.kind === 'item' || column.kind === 'model' ? (
												<Select
													size="sm"
													aria-label={t(column.label)}
													value={row[column.key] ?? ''}
													placeholder={t('Choose')}
													options={(column.kind === 'item'
														? (lookups?.items ?? [])
														: (lookups?.models ?? [])
													).map((option) => ({
														value: String(option.id),
														label: t(option.name),
													}))}
													onChange={(next) => {
														update(index, column.key, next);
													}}
													disabled={disabled}
												/>
											) : (
												<Input
													size="sm"
													aria-label={t(column.label)}
													type={column.kind === 'text' ? 'text' : 'number'}
													step={column.kind === 'currency' ? '0.01' : column.kind === 'number' ? '1' : undefined}
													min={column.kind === 'text' ? undefined : 0}
													value={row[column.key] ?? ''}
													onChange={(event) => {
														update(index, column.key, event.target.value);
													}}
													disabled={disabled}
												/>
											)}
										</td>
									))}
									<td className="py-1 align-top">
										<button
											type="button"
											aria-label={t('Remove row')}
											className={cn('p-2 text-sm', colors.classes.textSecondary)}
											onClick={() => {
												onChange(rows.filter((_, rowIndex) => rowIndex !== index));
											}}
											disabled={disabled}
										>
											<FontAwesomeIcon icon={faTrash} />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{rows.length < maxRows && (
				<Button
					type="button"
					variant="outline"
					size="xs"
					onClick={() => {
						onChange([...rows, {}]);
					}}
					disabled={disabled}
				>
					<FontAwesomeIcon icon={faPlus} className="mr-1.5" />
					{t('Add row')}
				</Button>
			)}
		</div>
	);
}
